import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  FormControlLabel,
  Switch,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Datagrid,
  FunctionField,
  List,
  useNotify,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  StApiError,
  stConfigurePool,
  stDisableRoomPool,
  stListRooms,
  stPoolConfig,
  stSettlementCycles,
  StI18nEntry,
  StPoolConfig,
  StRoom,
  StRoomPoolListItem,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { I18nFieldBlock } from '../I18nFieldBlock';
import { MembersDialog } from './MembersDialog';
import { emptyLangPack, isLangPackComplete, packFromEntries, syncZh } from '../i18nLang';
import { StActionButton, StActionRow, StBooleanChip, StStatusChip, stFormatDateTime, stText } from '../StListUi';
import { StPoolSettleConfirmContent } from '../StPoolSettleConfirmContent';
import {
  hasPositiveRewardAmount,
  poolFormFromItem,
  poolSaveError,
  toConfigurePoolBody,
  validatePoolForm,
} from './helpers';
import './init';

/** Check if a value is a positive number (> 0) */
const isPositiveNumber = (value: string): boolean => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0;
};

type PoolForm = {
  roomId: string;
  cycleStart: string;
  cycleEnd: string;
  cycleLabel: string;
  cycleLabelLang: StI18nEntry[];
  cycleLabelLangDirty: boolean;
  i18nStale: boolean;
  volumeAmount: string;
  profitAmount: string;
  volumeTarget: string;
  profitTarget: string;
  // V2.5 item 7 — the two pools unlock independently, each with its own
  // personal-participation rule.
  volumeEnabled: boolean;
  profitEnabled: boolean;
  requirePersonalVolume: boolean;
  requirePersonalProfit: boolean;
  minPersonalVolume: string;
  minPersonalProfit: string;
};

type DialogMode = 'create' | 'edit';

type PoolSide = {
  key: 'volume' | 'profit';
  titleKey: string;
  enabledKey: 'volumeEnabled' | 'profitEnabled';
  requireKey: 'requirePersonalVolume' | 'requirePersonalProfit';
  amountKey: 'volumeAmount' | 'profitAmount';
  targetKey: 'volumeTarget' | 'profitTarget';
  minKey: 'minPersonalVolume' | 'minPersonalProfit';
  amountLabelKey: string;
  targetLabelKey: string;
  minLabelKey: string;
  globalOf: (config: StPoolConfig | null) => string | undefined;
};

const POOL_SIDES: PoolSide[] = [
  {
    key: 'volume',
    titleKey: 'module.socialTrading.poolVolumeSection',
    enabledKey: 'volumeEnabled',
    requireKey: 'requirePersonalVolume',
    amountKey: 'volumeAmount',
    targetKey: 'volumeTarget',
    minKey: 'minPersonalVolume',
    amountLabelKey: 'module.socialTrading.poolFieldVolumeAmount',
    targetLabelKey: 'module.socialTrading.poolFieldVolumeTarget',
    minLabelKey: 'module.socialTrading.poolFieldMinPersonalVolume',
    globalOf: (config) => config?.minVolumeParticipation,
  },
  {
    key: 'profit',
    titleKey: 'module.socialTrading.poolProfitSection',
    enabledKey: 'profitEnabled',
    requireKey: 'requirePersonalProfit',
    amountKey: 'profitAmount',
    targetKey: 'profitTarget',
    minKey: 'minPersonalProfit',
    amountLabelKey: 'module.socialTrading.poolFieldProfitAmount',
    targetLabelKey: 'module.socialTrading.poolFieldProfitTarget',
    minLabelKey: 'module.socialTrading.poolFieldMinPersonalProfit',
    globalOf: (config) => config?.minProfitParticipation,
  },
];

function toLocalInputValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function emptyPoolForm(): PoolForm {
  const start = new Date();
  const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    roomId: '',
    cycleStart: toLocalInputValue(start),
    cycleEnd: toLocalInputValue(end),
    cycleLabel: '',
    cycleLabelLang: emptyLangPack(),
    cycleLabelLangDirty: false,
    i18nStale: false,
    // 默认值：交易量奖励 = 100，交易量解锁目标 = 1,000,000，最低个人交易量 = 100
    volumeAmount: '100',
    volumeTarget: '1000000',
    minPersonalVolume: '100',
    // 默认值：利润奖励 = 100，利润解锁目标 = 10,000，最低个人利润 = 1
    profitAmount: '100',
    profitTarget: '10000',
    minPersonalProfit: '1',
    volumeEnabled: true,
    profitEnabled: true,
    requirePersonalVolume: true,
    requirePersonalProfit: true,
  };
}

function toISO(local: string) {
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return local;
  return d.toISOString();
}

function fromISOToLocal(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return toLocalInputValue(d);
}

function isCycleStartLocked(cycleStartLocal: string): boolean {
  const d = new Date(cycleStartLocal);
  if (Number.isNaN(d.getTime())) return false;
  return Date.now() >= d.getTime();
}

export default function SocialTradingPoolsList() {
  const t = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [rooms, setRooms] = useState<StRoom[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [form, setForm] = useState<PoolForm>(emptyPoolForm());
  const [globalConfig, setGlobalConfig] = useState<StPoolConfig | null>(null);
  // Which field the last save failed on, from the upstream code (V2.5 item 5).
  const [saveError, setSaveError] = useState<{ field: string; message: string } | null>(null);
  const [membersPool, setMembersPool] = useState<StRoomPoolListItem | null>(null);
  const [disableTarget, setDisableTarget] = useState<string | null>(null);
  const [disabling, setDisabling] = useState(false);
  const editing = dialogMode === 'edit';
  const cycleStartLocked = editing && isCycleStartLocked(form.cycleStart);

  // The global pair prefills a new cycle and backs the "inherited" placeholder
  // on a cycle saved before per-pool thresholds existed.
  const ensureGlobalConfig = async (): Promise<StPoolConfig | null> => {
    if (globalConfig) return globalConfig;
    try {
      const cfg = await stPoolConfig();
      setGlobalConfig(cfg || null);
      return cfg || null;
    } catch {
      return null;
    }
  };

  const ensureRooms = async () => {
    if (rooms.length) return;
    try {
      const r = await stListRooms({ limit: 100 });
      setRooms((r?.items || []).filter((room) => room.type !== 'KOL_ROOM'));
    } catch {
      setRooms([]);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setForm(emptyPoolForm());
    setDialogMode('create');
    setSaveError(null);
  };

  const openCreate = async () => {
    await ensureRooms();
    await ensureGlobalConfig();
    setDialogMode('create');
    setSaveError(null);
    setForm(emptyPoolForm());
    setDialogOpen(true);
  };

  const openEdit = async (p: StRoomPoolListItem) => {
    await ensureRooms();
    await ensureGlobalConfig();
    setSaveError(null);
    let volumeTarget = p.volumeTarget ?? '';
    let profitTarget = p.profitTarget ?? '';
    if (!volumeTarget || !profitTarget) {
      try {
        // PoolPhase filter: UPCOMING cycles are no longer returned by status=ACTIVE.
        const cycles = await stSettlementCycles({
          roomId: p.roomId,
          status: p.status === 'UPCOMING' ? 'UPCOMING' : 'ACTIVE',
          limit: 1,
          offset: 0,
        });
        const active = cycles?.items?.[0];
        volumeTarget = volumeTarget || active?.volumeTarget || '';
        profitTarget = profitTarget || active?.profitTarget || '';
      } catch {
        /* targets optional */
      }
    }
    setDialogMode('edit');
    setForm({
      ...poolFormFromItem(p),
      roomId: p.roomId,
      cycleStart: fromISOToLocal(p.cycleStart),
      cycleEnd: fromISOToLocal(p.cycleEnd),
      cycleLabel: p.cycleLabel || '',
      cycleLabelLang: packFromEntries(p.cycleLabelLang, p.cycleLabel || ''),
      cycleLabelLangDirty: false,
      i18nStale: false,
      volumeAmount: p.volumeAmount ?? '0',
      profitAmount: p.profitAmount ?? '0',
      volumeTarget,
      profitTarget,
    });
    setDialogOpen(true);
  };

  const disablePool = async () => {
    if (!disableTarget) return;
    setDisabling(true);
    try {
      await stDisableRoomPool(disableTarget);
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      setDisableTarget(null);
      refresh();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    } finally {
      setDisabling(false);
    }
  };

  const savePool = async () => {
    if (!form.roomId) {
      notify(t('module.socialTrading.poolNeedRoom'), { type: 'warning' });
      return;
    }
    const label = form.cycleLabel.trim();
    const needLang = dialogMode === 'create' || form.cycleLabelLangDirty;
    if (needLang && !isLangPackComplete(syncZh(form.cycleLabelLang, label), !label)) {
      notify(t('module.socialTrading.i18nPackIncomplete'), { type: 'warning' });
      return;
    }
    if (!hasPositiveRewardAmount(form.volumeAmount, form.profitAmount)) {
      notify(t('module.socialTrading.poolNeedRewardAmount'), { type: 'warning' });
      return;
    }
    const invalid = validatePoolForm(form);
    if (invalid) {
      const message = t(`module.socialTrading.poolErrors.${invalid.code}`);
      setSaveError({ field: invalid.field, message });
      notify(message, { type: 'warning' });
      return;
    }

    setSaveError(null);
    try {
      await stConfigurePool({
        ...toConfigurePoolBody({
          ...form,
          cycleStart: toISO(form.cycleStart),
          cycleEnd: toISO(form.cycleEnd),
          cycleLabel: label || undefined,
        }),
        ...(needLang ? { cycleLabelLang: syncZh(form.cycleLabelLang, label) } : {}),
      });
      closeDialog();
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      refresh();
    } catch (e: any) {
      // The field-level code lives in `upstreamCode`; `code` is the same envelope
      // for every one of them.
      const mapped = poolSaveError(e instanceof StApiError ? e.upstreamCode : undefined);
      const message = mapped ? t(mapped.messageKey) : stErrorMessage(e);
      if (mapped) setSaveError({ field: mapped.field, message });
      notify(message, { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-room-pools" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.roomPools')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.pools.root },
            { name: t('module.socialTrading.roomPools') },
          ]}
          action={
            <PermissionButton resource="st-room-pools" action="update">
              <Button size="small" variant="contained" onClick={openCreate}>
                {t('module.socialTrading.createPool')}
              </Button>
            </PermissionButton>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <List
            resource={Resources.socialTradingPools}
            actions={false}
            empty={false}
            perPage={10}
            exporter={false}
          >
            <Box sx={{ overflow: 'auto' }}>
              <Datagrid rowClick={false} bulkActionButtons={false}>
                <FunctionField
                  label="room"
                  render={(r: StRoomPoolListItem) => stText(r.roomName)}
                />
                <FunctionField
                  label="type"
                  render={(r: StRoomPoolListItem) => stText(r.roomType)}
                />
                <FunctionField
                  label="enabled"
                  render={(r: StRoomPoolListItem) => <StBooleanChip value={r.poolEnabled} />}
                />
                <FunctionField
                  label="status"
                  render={(r: StRoomPoolListItem) => <StStatusChip status={r.status} />}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldCycleStart')}
                  render={(r: StRoomPoolListItem) => stText(stFormatDateTime(r.cycleStart))}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldCycleEnd')}
                  render={(r: StRoomPoolListItem) => stText(stFormatDateTime(r.cycleEnd))}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldVolumeAmount')}
                  render={(r: StRoomPoolListItem) => stText(r.volumeAmount)}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldVolumeTarget')}
                  render={(r: StRoomPoolListItem) => stText(r.volumeTarget)}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldProfitAmount')}
                  render={(r: StRoomPoolListItem) => stText(r.profitAmount)}
                />
                <FunctionField
                  label={t('module.socialTrading.poolFieldProfitTarget')}
                  render={(r: StRoomPoolListItem) => stText(r.profitTarget)}
                />
                <FunctionField
                  label={t('module.socialTrading.actions')}
                  render={(r: StRoomPoolListItem) => {
                    if (!r.poolEnabled) return null;
                    const canEdit = r.status === 'UPCOMING' || r.status === 'ACTIVE';
                    return (
                      <StActionRow>
                        {canEdit && (
                          <PermissionButton resource="st-room-pools" action="update">
                            <StActionButton onClick={() => openEdit(r)}>
                              {t('module.socialTrading.edit')}
                            </StActionButton>
                          </PermissionButton>
                        )}
                        <StActionButton onClick={() => setMembersPool(r)}>
                          {t('module.socialTrading.poolMembers')}
                        </StActionButton>
                        <PermissionButton resource="st-room-pools" action="update">
                          <StActionButton color="warning" onClick={() => setDisableTarget(r.roomId)}>
                            {t('module.socialTrading.disablePool')}
                          </StActionButton>
                        </PermissionButton>
                      </StActionRow>
                    );
                  }}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>

        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            {t(editing ? 'module.socialTrading.editPool' : 'module.socialTrading.createPool')}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {rooms.length > 0 ? (
                <>
                  <TextField
                    select
                    size="small"
                    label={t('module.socialTrading.poolFieldRoom')}
                    required
                    disabled={editing}
                    value={form.roomId}
                    onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                  >
                    {rooms.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.name} ({r.type})
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    size="small"
                    label={t('module.socialTrading.poolFieldRoomIdReadonly')}
                    value={form.roomId}
                    InputProps={{ readOnly: true }}
                    disabled
                  />
                </>
              ) : (
                <TextField
                  size="small"
                  label={t('module.socialTrading.poolFieldRoomId')}
                  required
                  disabled={editing}
                  value={form.roomId}
                  onChange={(e) => setForm({ ...form, roomId: e.target.value })}
                  helperText={t('module.socialTrading.poolRoomManual')}
                />
              )}
              {form.i18nStale && (
                <Alert severity="warning">{t('module.socialTrading.i18nStaleHint')}</Alert>
              )}
              <I18nFieldBlock
                fieldKey="cycleLabel"
                label={t('module.socialTrading.poolFieldCycleLabel')}
                primary={form.cycleLabel}
                onPrimaryChange={(cycleLabel) => setForm({ ...form, cycleLabel })}
                pack={form.cycleLabelLang}
                onPackChange={(cycleLabelLang) =>
                  setForm({
                    ...form,
                    cycleLabelLang,
                    cycleLabelLangDirty: true,
                    i18nStale: false,
                  })
                }
                onPrimaryEditedAfterFill={() => setForm((prev) => ({ ...prev, i18nStale: true }))}
                allowEmptyFill
              />
              <TextField
                size="small"
                type="datetime-local"
                label={t('module.socialTrading.poolFieldCycleStart')}
                InputLabelProps={{ shrink: true }}
                disabled={cycleStartLocked}
                value={form.cycleStart}
                onChange={(e) => setForm({ ...form, cycleStart: e.target.value })}
              />
              <TextField
                size="small"
                type="datetime-local"
                label={t('module.socialTrading.poolFieldCycleEnd')}
                InputLabelProps={{ shrink: true }}
                value={form.cycleEnd}
                onChange={(e) => setForm({ ...form, cycleEnd: e.target.value })}
              />
              {saveError?.field === 'form' && <Alert severity="error">{saveError.message}</Alert>}

              {/* The two pools unlock independently — each owns its amount,
                  target and personal-participation rule. */}
              {POOL_SIDES.map((side) => {
                const enabled = form[side.enabledKey];
                const amountError = enabled && !isPositiveNumber(form[side.amountKey]);
                const targetError = enabled && !isPositiveNumber(form[side.targetKey]);
                const requirePersonal = form[side.requireKey];
                const minError = enabled && requirePersonal && !isPositiveNumber(form[side.minKey]);

                return (
                  <Box
                    key={side.key}
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={form[side.enabledKey]}
                          onChange={(e) => setForm({ ...form, [side.enabledKey]: e.target.checked })}
                        />
                      }
                      label={t(side.titleKey)}
                    />
                    <Stack spacing={1.5} sx={{ mt: 1, opacity: form[side.enabledKey] ? 1 : 0.5 }}>
                      <Stack direction="row" spacing={1}>
                        <TextField
                          size="small"
                          fullWidth
                          disabled={!form[side.enabledKey]}
                          label={t(side.amountLabelKey)}
                          value={form[side.amountKey]}
                          onChange={(e) => setForm({ ...form, [side.amountKey]: e.target.value })}
                          error={amountError || saveError?.field === side.amountKey}
                          helperText={
                            amountError
                              ? t('module.socialTrading.poolErrors.mustBePositive', {
                                  field: t(side.amountLabelKey),
                                })
                              : saveError?.field === side.amountKey
                                ? saveError.message
                                : ' '
                          }
                        />
                        <TextField
                          size="small"
                          fullWidth
                          disabled={!form[side.enabledKey]}
                          label={t(side.targetLabelKey)}
                          value={form[side.targetKey]}
                          onChange={(e) => setForm({ ...form, [side.targetKey]: e.target.value })}
                          error={targetError || saveError?.field === side.targetKey}
                          helperText={
                            targetError
                              ? t('module.socialTrading.poolErrors.mustBePositive', {
                                  field: t(side.targetLabelKey),
                                })
                              : saveError?.field === side.targetKey
                                ? saveError.message
                                : ' '
                          }
                        />
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="flex-start">
                        <FormControlLabel
                          sx={{ minWidth: 170 }}
                          control={
                            <Switch
                              size="small"
                              disabled={!form[side.enabledKey]}
                              checked={form[side.requireKey]}
                              onChange={(e) =>
                                setForm({ ...form, [side.requireKey]: e.target.checked })
                              }
                            />
                          }
                          label={t('module.socialTrading.poolRequirePersonal')}
                        />
                        <TextField
                          size="small"
                          fullWidth
                          disabled={!form[side.enabledKey] || !form[side.requireKey]}
                          label={t(side.minLabelKey)}
                          value={form[side.minKey]}
                          onChange={(e) => setForm({ ...form, [side.minKey]: e.target.value })}
                          placeholder={side.globalOf(globalConfig) ?? ''}
                          error={minError || saveError?.field === side.minKey}
                          helperText={
                            minError
                              ? t('module.socialTrading.poolErrors.mustBePositive', {
                                  field: t(side.minLabelKey),
                                })
                              : saveError?.field === side.minKey
                                ? saveError.message
                                : t('module.socialTrading.poolMinPersonalHint', {
                                    value: side.globalOf(globalConfig) ?? '—',
                                  })
                          }
                        />
                      </Stack>
                    </Stack>
                  </Box>
                );
              })}
              <Typography variant="caption" color="text.secondary">
                {t('module.socialTrading.poolOverwriteHint')}
              </Typography>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>{t('module.socialTrading.cancel')}</Button>
            <PermissionButton resource="st-room-pools" action="update">
              <Button
                variant="contained"
                onClick={savePool}
                disabled={!form.roomId || POOL_SIDES.some((side) => {
                  if (!form[side.enabledKey]) return false;
                  return (
                    !isPositiveNumber(form[side.amountKey]) ||
                    !isPositiveNumber(form[side.targetKey]) ||
                    (form[side.requireKey] && !isPositiveNumber(form[side.minKey]))
                  );
                })}
              >
                {t('module.socialTrading.save')}
              </Button>
            </PermissionButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={!!membersPool}
          onClose={() => setMembersPool(null)}
          fullWidth
          maxWidth="md"
        >
          {membersPool && (
            <MembersDialog pool={membersPool} onClose={() => setMembersPool(null)} />
          )}
        </Dialog>

        <ConfirmDialog
          open={!!disableTarget}
          onClose={() => !disabling && setDisableTarget(null)}
          title={t('module.socialTrading.disablePool')}
          content={
            <StPoolSettleConfirmContent
              message={t('module.socialTrading.confirmDisablePool')}
              settleNote={t('module.socialTrading.confirmDisablePoolSettle')}
            />
          }
          action={
            <PermissionButton resource="st-room-pools" action="update">
              <Button variant="contained" color="error" disabled={disabling} onClick={disablePool}>
                {t('module.socialTrading.confirmDelete')}
              </Button>
            </PermissionButton>
          }
        />
      </DashboardContent>
    </PermissionGuard>
  );
}
